import type { CurrentUser } from "@/lib/auth";
import { userHasGroup, userIsBuero } from "@/lib/auth";
import { completionRoute, materialRequestRoute, timesheetRoute } from "@/features/tours/routes";
import type { TourDef } from "@/features/tours/types";

const COMMIT =
  "Die Führung klickt das nicht für dich. Nur wenn du die Schaltfläche selbst drückst, wird etwas angelegt.";

function materialSaveBody(user: CurrentUser): string {
  const canProcess =
    userIsBuero(user) || userHasGroup(user, "App-Admins") || userHasGroup(user, "App-Tester");
  if (canProcess) {
    return `Speichern legt die Anforderung an. Danach kannst du sie im Büro weiterverarbeiten. ${COMMIT}`;
  }
  return `Speichern gibt die Anforderung ans Büro. ${COMMIT}`;
}

/**
 * Große Aufgaben. Jede Führung zeigt nur Schritte, deren Seite die Rolle öffnen darf.
 * Abschluss-Schaltflächen (Speichern, Versenden, Einreichen) werden gezeigt und erklärt,
 * aber nicht als Pflichtklick verlangt.
 */
export const TOURS: TourDef[] = [
  {
    id: "rechnung-schreiben",
    title: "Rechnung schreiben",
    summary: "Projekt wählen und einen Rechnungsentwurf anlegen.",
    aclHref: "/dokumente.html",
    steps: [
      {
        id: "neu",
        target: "dokument-neu",
        title: "Neue Rechnung",
        body: "Dieser Button öffnet den Dialog. Der Typ ist schon auf Rechnung gestellt.",
        route: "/dokumente/rechnungen",
        advance: "click",
      },
      {
        id: "projekt",
        target: "dokument-projekt",
        title: "Projekt wählen",
        body: "Suche das Projekt und wähle es aus. Erst dann lässt sich die Rechnung anlegen.",
        route: "/dokumente/rechnungen",
        advance: "next",
      },
      {
        id: "typ",
        target: "dokument-typ-rechnung",
        title: "Typ Rechnung",
        body: "Rechnung ist bereits gewählt. Andere Karten brauchst du für diese Aufgabe nicht.",
        route: "/dokumente/rechnungen",
        advance: "next",
      },
      {
        id: "erstellen",
        target: "dokument-erstellen",
        title: "Entwurf anlegen",
        body: `„Dokument erstellen“ speichert einen Entwurf, noch keine versendete Rechnung. ${COMMIT}`,
        route: "/dokumente/rechnungen",
        advance: "next",
      },
    ],
  },
  {
    id: "angebot-schreiben",
    title: "Angebot schreiben",
    summary: "Projekt wählen und einen Angebotsentwurf anlegen.",
    aclHref: "/dokumente.html",
    steps: [
      {
        id: "neu",
        target: "dokument-neu",
        title: "Neues Angebot",
        body: "Dieser Button öffnet den Dialog. Der Typ ist schon auf Angebot gestellt.",
        route: "/dokumente/angebote",
        advance: "click",
      },
      {
        id: "projekt",
        target: "dokument-projekt",
        title: "Projekt wählen",
        body: "Suche das Projekt und wähle es aus. Kunde und Texte kommen daraus.",
        route: "/dokumente/angebote",
        advance: "next",
      },
      {
        id: "typ",
        target: "dokument-typ-angebot",
        title: "Typ Angebot",
        body: "Angebot ist bereits gewählt. Darunter kannst du ein vorhandenes Angebot als Vorlage nutzen.",
        route: "/dokumente/angebote",
        advance: "next",
      },
      {
        id: "erstellen",
        target: "dokument-erstellen",
        title: "Entwurf anlegen",
        body: `„Dokument erstellen“ speichert einen Entwurf. Verschickt wird das Angebot erst später. ${COMMIT}`,
        route: "/dokumente/angebote",
        advance: "next",
      },
    ],
  },
  {
    id: "auftragsbestaetigung-schreiben",
    title: "Auftragsbestätigung schreiben",
    summary: "Aus einem Projekt eine Auftragsbestätigung anlegen.",
    aclHref: "/dokumente.html",
    steps: [
      {
        id: "neu",
        target: "dokument-neu",
        title: "Neues Dokument",
        body: "Öffne den Dialog über diesen Button.",
        route: "/dokumente",
        advance: "click",
      },
      {
        id: "projekt",
        target: "dokument-projekt",
        title: "Projekt wählen",
        body: "Zuerst das Projekt suchen und auswählen. Danach wird der Typ freigeschaltet.",
        route: "/dokumente",
        advance: "next",
      },
      {
        id: "typ",
        target: "dokument-typ-auftragsbestaetigung",
        title: "Auftragsbestätigung",
        body: "Klicke die Karte „Auftragsbestätigung“. Liegt schon ein Angebot im Projekt, schlägt die App es als Vorlage vor.",
        route: "/dokumente",
        advance: "click",
      },
      {
        id: "erstellen",
        target: "dokument-erstellen",
        title: "Entwurf anlegen",
        body: `„Dokument erstellen“ speichert die Auftragsbestätigung als Entwurf. ${COMMIT}`,
        route: "/dokumente",
        advance: "next",
      },
    ],
  },
  {
    id: "lieferschein-schreiben",
    title: "Lieferschein schreiben",
    summary: "Einen Lieferschein als Dokument zum Projekt anlegen.",
    aclHref: "/dokumente.html",
    steps: [
      {
        id: "neu",
        target: "dokument-neu",
        title: "Neues Dokument",
        body: "Öffne den Dialog über diesen Button.",
        route: "/dokumente",
        advance: "click",
      },
      {
        id: "projekt",
        target: "dokument-projekt",
        title: "Projekt wählen",
        body: "Suche das Projekt und wähle es aus.",
        route: "/dokumente",
        advance: "next",
      },
      {
        id: "typ",
        target: "dokument-typ-lieferschein",
        title: "Lieferschein",
        body: "Klicke die Karte „Lieferschein“. Eine Auftragsbestätigung oder ein Angebot im Projekt kann als Inhalt dienen.",
        route: "/dokumente",
        advance: "click",
      },
      {
        id: "erstellen",
        target: "dokument-erstellen",
        title: "Entwurf anlegen",
        body: `„Dokument erstellen“ speichert den Lieferschein als Entwurf. ${COMMIT}`,
        route: "/dokumente",
        advance: "next",
      },
    ],
  },
  {
    id: "stundennachweis-schreiben",
    title: "Stundennachweis schreiben",
    summary: "Projekt wählen, Zeiten eintragen und als Entwurf sichern.",
    aclHref: "/stundennachweise.html",
    steps: [
      {
        id: "neu",
        target: "stundennachweis-neu",
        title: "Neuen Nachweis starten",
        body: "„Neu“ öffnet die Projektauswahl.",
        route: timesheetRoute,
        advance: "click",
      },
      {
        id: "projekt",
        target: "stundennachweis-projekt",
        title: "Projekt wählen",
        body: "Suche das Projekt. Die Auftragsart entscheidet, welches Formular als Nächstes kommt.",
        route: timesheetRoute,
        advance: "next",
      },
      {
        id: "weiter",
        target: "stundennachweis-projekt-weiter",
        title: "Weiter",
        body: "Weiter öffnet den Nachweis zum gewählten Projekt.",
        route: timesheetRoute,
        advance: "click",
      },
      {
        id: "zeit",
        target: "stundennachweis-zeit",
        title: "Zeit eintragen",
        body: "„Manuelle Zeile hinzufügen“ erfasst Mitarbeiter, Datum und Stunden. Bei manchen Auftragsarten gibt es dieses Feld nicht.",
        route: timesheetRoute,
        advance: "click",
        optional: true,
      },
      {
        id: "entwurf",
        target: "stundennachweis-entwurf",
        title: "Als Entwurf speichern",
        body: `„Als Entwurf speichern“ hält den Nachweis, ohne ihn fertigzustellen. „Fertigstellen“ schließt ihn ab. ${COMMIT}`,
        route: timesheetRoute,
        advance: "next",
        optional: true,
      },
    ],
  },
  {
    id: "fertigstellung-schreiben",
    title: "Fertigstellung melden",
    summary: "Ein abgeschlossenes Projekt als Fertigstellung erfassen.",
    aclHref: "/stundennachweise.html",
    steps: [
      {
        id: "tab",
        target: "stundennachweis-tab-fertigstellung",
        title: "Fertigstellungen",
        body: "Wechsle auf Fertigstellungsmeldungen.",
        route: completionRoute,
        advance: "click",
        allow: (user) => !userIsBuero(user),
      },
      {
        id: "neu",
        target: "stundennachweis-neu",
        title: "Fertigstellung anlegen",
        body: "Dieser Button startet die Meldung mit der Projektauswahl.",
        route: completionRoute,
        advance: "click",
      },
      {
        id: "projekt",
        target: "stundennachweis-projekt",
        title: "Projekt wählen",
        body: "Suche das Projekt, das fertiggestellt wird.",
        route: completionRoute,
        advance: "next",
      },
      {
        id: "weiter",
        target: "stundennachweis-projekt-weiter",
        title: "Weiter",
        body: "Weiter öffnet die Fertigstellung. Unterschreiben und speichern machst du dort selbst.",
        route: completionRoute,
        advance: "click",
      },
    ],
  },
  {
    id: "material-schreiben",
    title: "Materialanforderung schreiben",
    summary: "Auftrag, Position oder Foto erfassen und speichern.",
    aclHref: "/materialanforderungen.html",
    steps: [
      {
        id: "neu",
        target: "material-neu",
        title: "Neue Anforderung",
        body: "Öffne eine leere Materialanforderung.",
        route: materialRequestRoute,
        advance: "click",
      },
      {
        id: "auftrag",
        target: "material-auftrag",
        title: "Auftrag wählen",
        body: "Ohne Auftrag kann die Anforderung nicht zugeordnet werden. Suche Nummer, Kunde oder Ort.",
        route: materialRequestRoute,
        advance: "next",
      },
      {
        id: "position",
        target: "material-position",
        title: "Position oder Foto",
        body: "„+ Position“ für eine Liste. „Bild / PDF anhängen“ reicht, wenn eine Skizze oder ein Foto genügt.",
        route: materialRequestRoute,
        advance: "click",
      },
      {
        id: "speichern",
        target: "material-speichern",
        title: "Speichern",
        body: materialSaveBody,
        route: materialRequestRoute,
        advance: "next",
      },
    ],
  },
  {
    id: "bestellung-anlegen",
    title: "Bestellung anlegen",
    summary: "Lieferant wählen und die Bestellung als Entwurf sichern.",
    aclHref: "/bestellungen.html",
    steps: [
      {
        id: "neu",
        target: "bestellung-neu",
        title: "Neue Bestellung",
        body: "Der Assistent öffnet sich über diesen Button.",
        route: "/bestellwesen/bestellungen",
        advance: "click",
      },
      {
        id: "lieferant",
        target: "bestellung-lieferant",
        title: "Lieferant",
        body: "Zuerst die Firma wählen. Positionen und Auftrag kommen danach.",
        route: "/bestellwesen/bestellungen",
        advance: "next",
      },
      {
        id: "entwurf",
        target: "bestellung-entwurf",
        title: "Entwurf speichern",
        body: "Entwurf speichern hält die Bestellung, ohne sie an den Lieferanten zu schicken.",
        route: "/bestellwesen/bestellungen",
        advance: "next",
      },
      {
        id: "absenden",
        target: "bestellung-absenden",
        title: "Artikel bestellen",
        body: `„Artikel bestellen“ löst die Bestellung aus. Der Button wird aktiv, sobald Positionen erfasst sind. ${COMMIT}`,
        route: "/bestellwesen/bestellungen",
        advance: "next",
      },
    ],
  },
  {
    id: "anfrage-schreiben",
    title: "Lieferantenanfrage schreiben",
    summary: "Eine Firma anfragen, ohne die Mail schon zu versenden.",
    aclHref: "/anfragen.html",
    steps: [
      {
        id: "neu",
        target: "anfrage-neu",
        title: "Neue Anfrage",
        body: "Öffne den Assistenten.",
        route: "/bestellwesen/anfragen",
        advance: "click",
      },
      {
        id: "lieferant",
        target: "anfrage-lieferant",
        title: "Lieferant",
        body: "Wähle die Firma aus den Kontakten oder gib eine E-Mail ein. Weitere Firmen kannst du darunter ergänzen.",
        route: "/bestellwesen/anfragen",
        advance: "next",
      },
      {
        id: "weiter",
        target: "anfrage-weiter",
        title: "Zu den Positionen",
        body: `Weiter wird aktiv, sobald ein Lieferant gewählt ist, und führt zu Auftrag und Positionen. Versendet wird erst auf der letzten Seite. ${COMMIT}`,
        route: "/bestellwesen/anfragen",
        advance: "next",
      },
    ],
  },
  {
    id: "wartung-anlegen",
    title: "Wartung anlegen",
    summary: "Vertrag anlegen und danach eine Wartung starten.",
    aclHref: "/wartungen/index.html",
    steps: [
      {
        id: "neu",
        target: "wartung-neu",
        title: "Neue Wartung",
        body: "Hier startest du eine Wartung. Ohne Vertrag oder Eintrag auf Zuruf bleibt der Button gesperrt.",
        route: "/wartungen",
        advance: "next",
      },
      {
        id: "vertrag",
        target: "wartung-vertrag",
        title: "Neuer Vertrag",
        body: "Ein Vertrag nennt Kunde, Orte und Kategorien. Öffne das Formular.",
        route: "/wartungen",
        advance: "click",
      },
      {
        id: "kunde",
        target: "wartung-kunde",
        title: "Kunde und Orte",
        body: "Zuerst den Kunden in den Kontakten suchen, danach mindestens einen Ort hinzufügen.",
        route: "/wartungen/vertrag-form",
        advance: "next",
      },
      {
        id: "speichern",
        target: "wartung-speichern",
        title: "Vertrag anlegen",
        body: `„Vertrag anlegen“ speichert den Vertrag. Danach kannst du unter Wartungen „Neue Wartung“ starten. ${COMMIT}`,
        route: "/wartungen/vertrag-form",
        advance: "next",
      },
    ],
  },
  {
    id: "abwesenheit-beantragen",
    title: "Abwesenheit beantragen",
    summary: "Urlaub oder eine andere Abwesenheit zur Freigabe einreichen.",
    aclHref: "/personal/me.html",
    steps: [
      {
        id: "tab",
        target: "abwesenheit-tab",
        title: "Abwesenheit",
        body: "Öffne den Bereich Abwesenheit.",
        route: "/personal/me",
        advance: "click",
      },
      {
        id: "art",
        target: "abwesenheit-art",
        title: "Art wählen",
        body: "Wähle zum Beispiel Urlaub, Krankheit oder Überstundenabbau.",
        route: "/personal/me",
        advance: "next",
      },
      {
        id: "von",
        target: "abwesenheit-von",
        title: "Beginn",
        body: "Trage den ersten Tag ein.",
        route: "/personal/me",
        advance: "next",
      },
      {
        id: "bis",
        target: "abwesenheit-bis",
        title: "Ende",
        body: "Trage den letzten Tag ein. Ein einzelner Tag hat dasselbe Datum in beiden Feldern.",
        route: "/personal/me",
        advance: "next",
      },
      {
        id: "einreichen",
        target: "abwesenheit-einreichen",
        title: "Antrag einreichen",
        body: `„Antrag einreichen“ schickt den Antrag an die Geschäftsführung. ${COMMIT}`,
        route: "/personal/me",
        advance: "next",
      },
    ],
  },
  {
    id: "mahnung-erstellen",
    title: "Mahnung erstellen",
    summary: "Überfällige Rechnungen anhaken und Entwürfe erzeugen.",
    aclHref: "/dokumente.html",
    steps: [
      {
        id: "liste",
        target: "mahnung-liste",
        title: "Rechnungen auswählen",
        body: "Hake die Forderungen an, die gemahnt werden sollen. Verschickt wird in diesem Schritt noch nichts.",
        route: "/dokumente/mahnwesen",
        advance: "next",
      },
      {
        id: "entwurf",
        target: "mahnung-entwurf",
        title: "Mahnentwürfe erstellen",
        body: `Der Button legt Entwürfe an. Prüfen, finalisieren und versenden kommt danach. ${COMMIT}`,
        route: "/dokumente/mahnwesen",
        advance: "next",
      },
    ],
  },
];
